import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: "", role: "", email: "", rating: 5, quote: "" };

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews`);
        if (!ignore) setReviews(data);
      } catch (e) {
        // Silently fail — show nothing, not an error page
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const setRating = (r) => setForm((f) => ({ ...f, rating: r }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role.trim() || undefined,
        email: form.email.trim() || undefined,
        rating: Number(form.rating),
        quote: form.quote.trim(),
      };
      await axios.post(`${API}/reviews`, payload);
      toast.success(
        "Thank you — your review was submitted and will appear after a quick review."
      );
      setForm(INITIAL);
      setOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please review the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const avg =
    reviews.length > 0
      ? (
          reviews.reduce((acc, r) => acc + (r.rating || 0), 0) /
          reviews.length
        ).toFixed(1)
      : null;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-28 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div className="reveal max-w-2xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline w-10" />
              <span className="eyebrow">Client Reviews</span>
            </div>
            <h2 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Kind words from{" "}
              <em className="text-[var(--accent)]">the people</em> we build
              for.
            </h2>
          </div>

          <div className="reveal reveal-delay-1 flex flex-col md:items-end gap-4">
            {avg && (
              <div
                className="flex items-center gap-3"
                data-testid="reviews-avg-rating"
              >
                <Stars value={Math.round(Number(avg))} />
                <div>
                  <div className="font-display text-2xl text-[var(--text-primary)] leading-none">
                    {avg} / 5
                  </div>
                  <div className="font-body text-[10px] uppercase tracking-[0.3em] text-[var(--text-secondary)] mt-1">
                    {reviews.length} review{reviews.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
            )}
            <button
              className="btn-outline w-fit"
              onClick={() => setOpen((v) => !v)}
              data-testid="reviews-toggle-form-btn"
            >
              {open ? "Close Form" : "Leave A Review"}
            </button>
          </div>
        </div>

        {/* Submission form */}
        {open && (
          <form
            onSubmit={submit}
            className="reveal border border-[var(--accent)]/30 p-8 md:p-12 mb-16 bg-[var(--bg-secondary)]"
            data-testid="review-form"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
              <h3 className="font-display text-3xl md:text-4xl">
                Share your <em className="text-[var(--accent)]">experience</em>.
              </h3>
              <div className="flex items-center gap-3" data-testid="review-rating-picker">
                <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[var(--text-secondary)]">
                  Your Rating
                </span>
                <RatingPicker value={form.rating} onChange={setRating} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <FieldLabel label="Your Name">
                <input
                  name="name"
                  className="line-input"
                  placeholder="First + last name"
                  value={form.name}
                  onChange={onChange}
                  required
                  minLength={1}
                  data-testid="review-input-name"
                />
              </FieldLabel>
              <FieldLabel label="Project Type (optional)">
                <input
                  name="role"
                  className="line-input"
                  placeholder="e.g. Homeowner · Custom Home"
                  value={form.role}
                  onChange={onChange}
                  data-testid="review-input-role"
                />
              </FieldLabel>
            </div>

            <div className="mb-8">
              <FieldLabel label="Email (optional — never shown publicly)">
                <input
                  name="email"
                  type="email"
                  className="line-input"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={onChange}
                  data-testid="review-input-email"
                />
              </FieldLabel>
            </div>

            <div className="mb-10">
              <FieldLabel label="Your Review">
                <textarea
                  name="quote"
                  rows={4}
                  className="line-input resize-none"
                  placeholder="Tell us about your experience with the build…"
                  value={form.quote}
                  onChange={onChange}
                  required
                  minLength={10}
                  maxLength={1200}
                  data-testid="review-input-quote"
                />
              </FieldLabel>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <button
                type="submit"
                className="btn-solid"
                disabled={submitting}
                data-testid="review-submit-btn"
              >
                {submitting ? "Submitting…" : "Submit Review"}
              </button>
              <p className="text-xs text-[var(--text-secondary)] max-w-xs">
                Reviews are verified before being published. You'll see yours
                on the site within 1 business day.
              </p>
            </div>
          </form>
        )}

        {/* Reviews grid */}
        {loading ? (
          <div
            className="text-center text-[var(--text-secondary)] font-body tracking-widest uppercase text-xs"
            data-testid="reviews-loading"
          >
            Loading reviews…
          </div>
        ) : reviews.length === 0 ? (
          <div
            className="text-center py-16 border border-white/10"
            data-testid="reviews-empty"
          >
            <p className="font-display italic text-2xl text-[var(--text-primary)] mb-4">
              Be the first to leave a review.
            </p>
            <button
              className="btn-outline"
              onClick={() => setOpen(true)}
              data-testid="reviews-empty-cta"
            >
              Leave A Review
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <figure
                key={r.id}
                className="reveal border border-white/10 p-8 md:p-10 flex flex-col bg-[var(--bg-primary)] hover:border-[var(--accent)]/40 transition-colors duration-500"
                style={{ transitionDelay: `${i * 90}ms` }}
                data-testid={`review-card-${r.id}`}
              >
                <Stars value={r.rating} />
                <blockquote className="font-display text-xl md:text-[22px] leading-snug text-[var(--text-primary)] flex-1 mt-6">
                  "{r.quote}"
                </blockquote>
                <figcaption className="mt-10 pt-6 border-t border-white/10">
                  <div className="font-body text-sm text-[var(--text-primary)]">
                    {r.name}
                  </div>
                  {r.role && (
                    <div className="font-body text-[10px] uppercase tracking-[0.3em] text-[var(--text-secondary)] mt-1">
                      {r.role}
                    </div>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[var(--text-secondary)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function Stars({ value = 0 }) {
  return (
    <div className="flex items-center gap-1" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={n <= value ? "var(--accent)" : "none"}
          stroke="var(--accent)"
          strokeWidth="1.5"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

function RatingPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          className="transition-transform duration-200 hover:scale-110"
          data-testid={`review-star-${n}`}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill={n <= value ? "var(--accent)" : "none"}
            stroke="var(--accent)"
            strokeWidth="1.5"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </button>
      ))}
    </div>
  );
}
